import type { CognitoUser, CognitoUserSession } from 'amazon-cognito-identity-js';
import { useEffect } from 'react';
import Pool from '@/services/auth/userPool';
import { useAuthStore } from '@/stores/slices/useAuthStore';

export const useSessionToken = (): void => {
  const getToken = useAuthStore((state) => state.getToken);
  const setGetToken = useAuthStore((state) => state.setGetToken);
  const setUser = useAuthStore((state) => state.setUser);

  useEffect(() => {
    if (!getToken) {
      const tokenFn = (): Promise<string> => {
        return new Promise<string>((resolve, reject) => {
          const current = Pool.getCurrentUser() as CognitoUser | null;
          if (current) {
            current.getSession((err: Error | null, session: CognitoUserSession | null) => {
              if (err || !session) {
                return reject(err || new Error('No session available'));
              }

              if (session.isValid()) {
                return resolve(session.getIdToken().getJwtToken());
              }

              // Session expired, try to refresh
              const refreshToken = session.getRefreshToken();
              if (!refreshToken) {
                return reject(new Error('No refresh token available'));
              }

              current.refreshSession(
                refreshToken,
                (refreshErr: Error | null, refreshedSession: CognitoUserSession | null) => {
                  if (refreshErr || !refreshedSession) {
                    return reject(refreshErr || new Error('Failed to refresh session'));
                  }

                  const idToken = refreshedSession.getIdToken();
                  const currentUser = useAuthStore.getState().user;
                  if (currentUser) {
                    const payload = idToken.payload || {};
                    const userName = (payload.name as string) || (payload['cognito:username'] as string) || '';

                    setUser({
                      ...currentUser,
                      idToken: {
                        jwtToken: idToken.getJwtToken(),
                        payload: {
                          name: userName,
                          ...payload,
                        },
                      },
                    });
                  }
                  return resolve(idToken.getJwtToken());
                },
              );
            });
          } else {
            reject(new Error('No current user'));
          }
        });
      };
      setGetToken(tokenFn);
    }
  }, [getToken, setGetToken, setUser]);
};
